// src/layouts/PrivateLayout.js
import React, { useState, useEffect, useRef } from 'react';
import {
  Navigate,
  Outlet,
  useNavigate,
  useLocation,
} from 'react-router-dom';
import axiosInstance from '../utils/axiosInstance';           // shared axios with auto-refresh
import AppSidebar from '../components/AppSidebar';

function PrivateLayout() {
  /* ------------------------------------------------------------------
     1.  Auth & user
  ------------------------------------------------------------------ */
  const token = localStorage.getItem('token');
  const [user, setUser] = useState(
    () => JSON.parse(localStorage.getItem('user')) || {}
  );

  /* ------------------------------------------------------------------
     2.  UI state
  ------------------------------------------------------------------ */
  const [showDropdown,   setShowDropdown]   = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [lang,           setLang]           = useState(
    () => localStorage.getItem('lang') || 'en'
  );
  const [showLangMenu,   setShowLangMenu]   = useState(false);

  /* Invite-friend */
  const [showInviteModal,        setShowInviteModal]        = useState(false);
  const [inviteEmail,            setInviteEmail]            = useState('');
  const [inviteError,            setInviteError]            = useState(null);
  const [showInviteSuccessModal, setShowInviteSuccessModal] = useState(false);

  /* Bank-update + logout toasts */
  const [bankUpdateToast, setBankUpdateToast] = useState(false);
  const [logoutToast,     setLogoutToast]     = useState(false);

  /* ------------------------------------------------------------------
     3.  Refs & router hooks
  ------------------------------------------------------------------ */
  const gearMenuRef = useRef(null);
  const langMenuRef = useRef(null);

  const navigate  = useNavigate();
  const location  = useLocation();

  /* ------------------------------------------------------------------
     4.  Global click & message listeners
  ------------------------------------------------------------------ */
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (gearMenuRef.current && !gearMenuRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
      if (langMenuRef.current && !langMenuRef.current.contains(e.target)) {
        setShowLangMenu(false);
      }
    };

    const handleMessage = (e) => {
      if (e.data === 'bankUpdated') {
        setBankUpdateToast(true);
        setTimeout(() => setBankUpdateToast(false), 5000);
      }
      if (e.data === 'logout_due_to_expiry') {
        setLogoutToast(true);
        setTimeout(() => {
          setLogoutToast(false);
          navigate('/');
        }, 3500);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('message',   handleMessage);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('message',   handleMessage);
    };
  }, [navigate]);

  /* ------------------------------------------------------------------
     5.  Detect Stripe return tab (?bankUpdated=1)
  ------------------------------------------------------------------ */
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('bankUpdated') === '1') {
      if (window.opener) {
        window.opener.postMessage('bankUpdated', '*');
        window.close();
        return;
      }
      setBankUpdateToast(true);
      setTimeout(() => setBankUpdateToast(false), 5000);
      params.delete('bankUpdated');
      navigate({ search: params.toString() }, { replace: true });
    }
  }, [location, navigate]);

  /* ------------------------------------------------------------------
     6.  Redirect if no JWT
  ------------------------------------------------------------------ */
  if (!token) {
    return <Navigate to="/" replace />;
  }

  /* ------------------------------------------------------------------
     7.  Helpers
  ------------------------------------------------------------------ */
  const langLabel = { en: 'English', ja: '日本語', pt: 'Português' };
  const switchLanguage = (l) => {
    setLang(l);
    setShowLangMenu(false);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/');
  };

  /* ------------------------------------------------------------------
     8.  BANK-LINK FLOW
  ------------------------------------------------------------------ */
  const openStripeBankLink = async () => {
    try {
      setShowDropdown(false);
      const { data } = await axiosInstance.post('/api/connect/bank-link');
      window.open(data.url, '_blank');
    } catch (err) {
      console.error('Error creating bank-link:', err);
      alert(
        err.response?.data?.message ||
          'Unable to open bank-update page. Please try again.'
      );
    }
  };

  /* ------------------------------------------------------------------
     9.  Dropdown translations
  ------------------------------------------------------------------ */
  const dropdownTranslations = {
    en: { updateBankDetails: 'Update Bank Details', logout: 'Logout' },
    ja: { updateBankDetails: '銀行口座情報を更新', logout: 'ログアウト' },
    pt: { updateBankDetails: 'Atualizar detalhes bancários', logout: 'Sair' },
  };
  const t = dropdownTranslations[lang] || dropdownTranslations.en;

  /* ------------------------------------------------------------------
    10.  Invite-friend logic
  ------------------------------------------------------------------ */
  const handleSendInvite = async () => {
    try {
      setInviteError(null);
      await axiosInstance.post('/api/invite', {
        friendEmail: inviteEmail,
        userName:    user.name,
      });
      setShowInviteModal(false);
      setShowInviteSuccessModal(true);
      setInviteEmail('');
    } catch (err) {
      console.error('Error sending invite:', err);
      setInviteError(err.response?.data?.message || 'Error sending invite');
    }
  };

  /* ------------------------------------------------------------------
    11.  JSX
  ------------------------------------------------------------------ */
  return (
    <div className="flex flex-col h-screen">
      {/* ===== TOP BAR ================================================= */}
      <header className="flex items-center bg-white px-4 py-2 shadow">
        <img src="/payme-logo.png" alt="PayMe" className="h-8 w-auto mr-4" />

        {/* Hamburger (mobile) */}
        <button
          className="md:hidden bg-blue-600 text-white px-3 py-2 rounded"
          onClick={() => setShowMobileMenu(true)}
        >
          ☰
        </button>

        <div className="flex-1" />

        {/* Invite Friend */}
        {user?.id && (
          <button
            type="button"
            onClick={() => setShowInviteModal(true)}
            className="text-green-500 bg-green-100 btn hover:bg-green-600 hover:text-white
                       focus:ring focus:ring-green-100 inline-flex items-center
                       text-sm px-4 py-2 rounded-md mr-4"
          >
            Invite Friend
          </button>
        )}

        {/* Language picker */}
        <div className="relative inline-block text-left mr-4" ref={langMenuRef}>
          <button
            onClick={() => setShowLangMenu(!showLangMenu)}
            className="inline-flex items-center gap-1 rounded-md border
                       border-gray-300 bg-white px-3 py-2 text-sm text-gray-700
                       hover:bg-gray-50 focus:outline-none"
          >
            {langLabel[lang]}
            <svg
              className="h-4 w-4"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {showLangMenu && (
            <div
              className="absolute right-0 z-10 mt-2 w-36 rounded-md bg-white
                         shadow ring-1 ring-black ring-opacity-5"
            >
              {['en', 'ja', 'pt'].map((l) => (
                <button
                  key={l}
                  className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                  onClick={() => switchLanguage(l)}
                >
                  {langLabel[l]}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Gear dropdown */}
        <div className="relative" ref={gearMenuRef}>
          <button onClick={() => setShowDropdown((p) => !p)}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="lucide lucide-settings"
            >
              <circle cx="12" cy="12" r="3" />
              <path d="M19.4 15a1 1 0 0 0 .76 1.3
                       2 2 0 1 1-1.9 2.6
                       1 1 0 0 0-1.21.79
                       2 2 0 1 1-3.86 0
                       1 1 0 0 0-1.33-.79
                       2 2 0 1 1-2.6-1.9
                       1 1 0 0 0-.79-1.21
                       2 2 0 1 1 0-3.86
                       1 1 0 0 0 .79-1.33
                       2 2 0 1 1 1.9-2.6
                       1 1 0 0 0 1.21-.76
                       2 2 0 1 1 3.86 0
                       1 1 0 0 0 1.33.76
                       2 2 0 1 1 2.6 1.9
                       1 1 0 0 0 .79 1.21
                       2 2 0 1 1 0 3.86Z" />
            </svg>
          </button>

          {showDropdown && (
            <div
              className="absolute right-0 mt-2 w-48 bg-white border
                         border-gray-200 rounded shadow z-50"
            >
              <button
                className="block w-full text-left px-4 py-2 text-sm
                           text-gray-700 hover:bg-gray-100"
                onClick={openStripeBankLink}
              >
                {t.updateBankDetails}
              </button>
              <button
                className="block w-full text-left px-4 py-2 text-sm
                           text-gray-700 hover:bg-gray-100"
                onClick={handleLogout}
              >
                {t.logout}
              </button>
            </div>
          )}
        </div>
      </header>

      {/* ===== MAIN LAYOUT ============================================ */}
      <div className="flex flex-1 overflow-hidden">
        <aside className="hidden md:block w-64 bg-gray-100 h-full">
          <AppSidebar lang={lang} />
        </aside>

        <main className="flex-1 bg-gray-100 p-4 overflow-y-auto">
          <Outlet context={{ lang, openInviteModal: () => setShowInviteModal(true) }} />
        </main>
      </div>

      {/* ===== MOBILE SIDEBAR ======================================== */}
      {showMobileMenu && (
        <div
          className="fixed inset-0 z-50 bg-black bg-opacity-40 md:hidden"
          onClick={() => setShowMobileMenu(false)}
        >
          <div
            className="absolute left-0 top-0 w-64 h-full bg-gray-100"
            onClick={(e) => e.stopPropagation()}
          >
            <AppSidebar
              onNavLinkClick={() => setShowMobileMenu(false)}
              lang={lang}
            />
          </div>
        </div>
      )}

      {/* ===== Invite Friend Modals =================================== */}
      {showInviteModal && (
        <InviteModal
          inviteEmail={inviteEmail}
          setInviteEmail={setInviteEmail}
          inviteError={inviteError}
          setInviteError={setInviteError}
          onClose={() => {
            setInviteEmail('');
            setInviteError(null);
            setShowInviteModal(false);
          }}
          onSend={handleSendInvite}
        />
      )}

      {showInviteSuccessModal && (
        <SuccessModal onClose={() => setShowInviteSuccessModal(false)} />
      )}

      {/* ===== Toasts ================================================ */}
      {bankUpdateToast && (
        <div className="fixed top-5 right-5 z-50 bg-green-600 text-white px-4 py-2 rounded shadow">
          Bank details updated successfully!
        </div>
      )}

      {logoutToast && (
        <div className="fixed top-5 right-5 z-50 bg-red-600 text-white px-4 py-2 rounded shadow">
          Session expired – please log in again.
        </div>
      )}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────── */
/* Separate tiny components keep the main body tidy                  */
/* ────────────────────────────────────────────────────────────────── */
function InviteModal({
  inviteEmail,
  setInviteEmail,
  inviteError,
  setInviteError,
  onClose,
  onSend,
}) {
  return (
    <div
      tabIndex="-1"
      className="fixed inset-0 z-50 flex items-center justify-center w-full
                 p-4 overflow-x-hidden overflow-y-auto bg-black bg-opacity-40"
    >
      <div className="relative w-full max-w-2xl">
        <div className="relative bg-white rounded-lg shadow dark:bg-gray-700">
          {/* Header */}
          <div className="flex items-start justify-between p-4 rounded-t bg-blue-700 text-white">
            <h3 className="text-xl font-semibold">Invite a Friend</h3>
            <button
              className="text-white bg-transparent hover:bg-blue-800 rounded-lg text-sm p-1.5 ml-auto inline-flex items-center"
              onClick={onClose}
            >
              <svg
                aria-hidden="true"
                className="w-5 h-5"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10
                     8.586l4.293-4.293a1 1 0 111.414 1.414L11.414
                     10l4.293 4.293a1 1 0 01-1.414 1.414L10
                     11.414l-4.293 4.293a1 1 0
                     01-1.414-1.414L8.586
                     10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
              <span className="sr-only">Close modal</span>
            </button>
          </div>

          {/* Body */}
          <div className="p-6 space-y-6">
            <label className="block mb-1 text-gray-700 font-medium">
              Friend's Email
            </label>
            <input
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              className="w-full border border-gray-300 rounded px-3 py-2"
              placeholder="Enter friend's email"
              required
            />
            {inviteError && (
              <p className="text-sm text-red-600">{inviteError}</p>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end p-6 space-x-2 border-t rounded-b">
            <button
              className="bg-blue-700 hover:bg-blue-800 text-white text-sm px-5 py-2.5 rounded"
              onClick={onSend}
            >
              Invite
            </button>
            <button
              className="text-sm px-5 py-2.5 rounded border"
              onClick={onClose}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function SuccessModal({ onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-sm p-6">
        <h4 className="text-xl font-semibold mb-4">Success</h4>
        <p>Your invite was sent successfully!</p>
        <div className="mt-6 flex justify-end">
          <button
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
            onClick={onClose}
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
}

export default PrivateLayout;
